import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import { Link, NavLink, useLocation } from 'react-router-dom';
import CountrySelector from '../CountryFlag/CountrySelector';

const Navbar = () => {
  const [isDark, setIsDark] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  const location = useLocation(); // Initialize useLocation hook
  const dropdownTimeoutRef = useRef(null); // New ref for timeout

  useEffect(() => {
    // Set initial theme
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');

    const handleScroll = () => {
      const header = document.querySelector('.genix-header');
      const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
      
      if (window.scrollY > 100) {
        header.style.background = isDarkMode ? 'rgba(26, 32, 44, 0.98)' : 'rgba(255, 255, 255, 0.98)';
        header.style.boxShadow = isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.2)' : '0 4px 20px rgba(0, 0, 0, 0.1)';
      } else {
        header.style.background = isDarkMode ? 'rgba(26, 32, 44, 0.95)' : 'rgba(255, 255, 255, 0.95)';
        header.style.boxShadow = 'none';
      }
    };

    const handleResize = () => {
      setScreenSize(window.innerWidth);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // Initial scroll check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isDark]); // Add isDark to dependencies

  // Handle body scroll lock
  useEffect(() => {
    if (isMenuOpen) {
      // Save current scroll position
      const currentScroll = window.pageYOffset;
      setScrollPosition(currentScroll);
      // Add styles to body
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${currentScroll}px`;
      document.body.style.width = '100%';
    } else {
      // Remove styles from body
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      // Restore scroll position
      window.scrollTo(0, scrollPosition);
    }
    // --- FIX: If cookie banner is open, always remove position: fixed ---
    if (document.body.classList.contains('cky-consent-banner-open')) {
      document.body.style.position = '';
    }
    // Cleanup function
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isMenuOpen, scrollPosition]);

  const handleThemeToggle = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    document.documentElement.setAttribute('data-theme', newIsDark ? 'dark' : 'light');
  };

  const handleSmoothScroll = (e, href) => {
    e.preventDefault();
    
    if (href === '#') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsProductsDropdownOpen(false);
  };

  const handleProductsHover = () => {
    if (!isMobileRange) { // Only for desktop hover
      clearTimeout(dropdownTimeoutRef.current); // Clear any pending close timeout
      setIsProductsDropdownOpen(true);
    }
  };

  const handleProductsLeave = () => {
    if (!isMobileRange) { // Only for desktop hover
      dropdownTimeoutRef.current = setTimeout(() => {
        setIsProductsDropdownOpen(false);
      }, 200); // Small delay to allow moving to dropdown
    }
  };

  const handleDropdownMenuEnter = () => {
    clearTimeout(dropdownTimeoutRef.current); // Clear timeout if mouse re-enters dropdown
  };

  const handleDropdownMenuLeave = () => {
    // Close immediately when leaving the dropdown menu itself
    setIsProductsDropdownOpen(false);
  };

  const toggleProductsDropdown = () => {
    setIsProductsDropdownOpen(!isProductsDropdownOpen);
  };

  // Check if we're in the 0px-500px range for mobile menu
  const isMobileRange = screenSize <= 500;

  return (
    <header className="genix-header">
      <div className="genix-container">
        <nav className="genix-nav">
          <img src="/images/GenixLogo.png" alt="Genix" className="genix-logo" />
          
          {/* Desktop Navigation Menu */}
          <ul className="genix-nav-menu">
            <li><NavLink to="/" className={({ isActive }) => isActive ? 'genix-active' : ''}>Home</NavLink></li>
            <li 
              className="genix-dropdown" 
              onMouseEnter={handleProductsHover} 
              onMouseLeave={handleProductsLeave}
            >
              <span 
                onClick={toggleProductsDropdown} 
                className={`genix-dropdown-toggle ${location.pathname.startsWith('/products') ? 'genix-active' : ''} ${isProductsDropdownOpen ? 'open' : ''}`}
              >
                Products <span className="genix-dropdown-arrow">▾</span>
              </span>
              {isProductsDropdownOpen && (
                <ul 
                  className={`genix-dropdown-menu ${isProductsDropdownOpen ? 'open' : ''}`}
                  onMouseEnter={handleDropdownMenuEnter} // New: Keep open when mouse enters dropdown
                  onMouseLeave={handleDropdownMenuLeave} // New: Close when mouse leaves dropdown
                >
                  <li><NavLink to="/products/genix-pendent" onClick={closeMenu}>Genix+Pendent</NavLink></li>
                  <li><NavLink to="/products/genix-hr" onClick={closeMenu}>Genix HR</NavLink></li>
                  <li><NavLink to="/products/genix-roata" onClick={closeMenu}>Genix Roata</NavLink></li>
                </ul>
              )}
            </li>
            <li><NavLink to="/resources" className={({ isActive }) => isActive ? 'genix-active' : ''}>Resources</NavLink></li>
            <li><NavLink to="/pricing" className={({ isActive }) => isActive ? 'genix-active' : ''}>Pricing</NavLink></li>
            <li><NavLink to="/contact" className={({ isActive }) => isActive ? 'genix-active' : ''}>Contact Us</NavLink></li>
          </ul>
          
          {/* Desktop Actions */}
          <div className="genix-nav-actions">
            <button className="genix-theme-toggle" onClick={handleThemeToggle}>
              {isDark ? '🌙' : '☀️'}
            </button>
            <CountrySelector />
            <NavLink to="/login" className="genix-btn genix-btn-secondary">Sign in</NavLink>
            <NavLink to="/book-demo" className="genix-btn genix-btn-primary-nav" style={{backgroundColor:'0000', color: '#0191c6'}}>Book a Demo</NavLink>
          </div>

          {/* Medium Screen Book Demo Button (500px-1200px) */}
          <div className="genix-medium-actions">
            <NavLink to="/book-demo" className="genix-btn genix-btn-primary" style={{position:'relative',left:'-29px'}}>Book a Demo</NavLink>
          </div>

          {/* Hamburger Menu Button - Always on right */}
          <button className="genix-hamburger" onClick={toggleMenu}>
            <span className={`genix-hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`genix-hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`genix-hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          </button>

          {/* Mobile Menu Overlay */}
          <div className={`genix-mobile-menu ${isMenuOpen ? 'open' : ''}`}>
            <div className="genix-mobile-menu-content">
              {/* Mobile Menu Items */}
              <ul className="genix-mobile-nav-menu">
                <li><NavLink to="/" className={({ isActive }) => isActive ? 'genix-active' : ''} onClick={closeMenu}>Home</NavLink></li>
                <li className="genix-dropdown-mobile">
                  <span 
                    onClick={toggleProductsDropdown} 
                    className={`genix-dropdown-toggle ${location.pathname.startsWith('/products') ? 'genix-active' : ''} ${isProductsDropdownOpen ? 'open' : ''}`}
                  >
                    Products <span className="genix-dropdown-arrow">▾</span>
                  </span>
                  {isProductsDropdownOpen && (
                    <ul 
                      className={`genix-mobile-dropdown-menu ${isProductsDropdownOpen ? 'open' : ''}`}
                      onMouseEnter={handleDropdownMenuEnter} // New: Keep open when mouse enters dropdown
                      onMouseLeave={handleDropdownMenuLeave} // New: Close when mouse leaves dropdown
                    >
                      <li><NavLink to="/products/genix-pendent" onClick={closeMenu}>Genix+Pendent</NavLink></li>
                      <li><NavLink to="/products/genix-hr" onClick={closeMenu}>Genix HR</NavLink></li>
                      <li><NavLink to="/products/genix-roata" onClick={closeMenu}>Genix Roata</NavLink></li>
                    </ul>
                  )}
                </li>
                <li><NavLink to="/resources" className={({ isActive }) => isActive ? 'genix-active' : ''} onClick={closeMenu}>Resources</NavLink></li>
                <li><NavLink to="/pricing" className={({ isActive }) => isActive ? 'genix-active' : ''} onClick={closeMenu}>Pricing</NavLink></li>
                <li><NavLink to="/contact" className={({ isActive }) => isActive ? 'genix-active' : ''} onClick={closeMenu}>Contact Us</NavLink></li>
              </ul>
              
              {/* Mobile Actions */}
              <div className="genix-mobile-actions">
                <button className="genix-theme-toggle" onClick={handleThemeToggle}>
                  {isDark ? '🌙' : '☀️'}
                </button>
                <CountrySelector />
                <NavLink to="/login" className="genix-btn genix-btn-secondary" onClick={closeMenu}>Sign in</NavLink>
                {/* Only show Book a Demo in mobile menu for 0px-500px */}
                {isMobileRange && (
                  <NavLink to="/book-demo" className="genix-btn genix-btn-primary" onClick={closeMenu}>Book a Demo</NavLink>
                )}
              </div>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar; 